/* eslint-disable max-lines */
import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, Input
} from '@angular/core';
import { Observable, zip } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';

import { HotelResultsFacade } from '@hotels/hotel-results/hotel-results.facade';
import { isEmpty } from '@app/util/util';
import { AppFacade } from '@app/app.facade';
import { getFiltersByBarWithCriteria } from '@shared/magic-filter-bar/hotel-filter-bar/mappers/hotel-filters-in-bar.mapper';
import {
  HotelFilterApplier, HotelFilterApplierValue, HotelFilterProvider, HotelFilterTypes
} from '@shared/hotel-itinerary/hotel-filters';
import { CheckboxFilter } from '@shared/filters/entities/fetch-filters.response';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import {
  HotelFilterBarBaseContainer
} from '@app/modules/shared/magic-filter-bar/hotel-filter-bar/abstracts/hotel-filter-bar-base.container';
import { singleFilterRoute as singleFilterRouteByHotels } from '@hotels/hotel-results/hotel-results-routing.module';
import { HotelFilterVm } from '@shared/magic-filter-bar/entities/filter-types';

@Component({
  selector: 'app-hotel-filter-bar-by-hotel',
  templateUrl: './hotel-filter-bar-by-hotel.container.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelFilterBarByHotelContainer extends HotelFilterBarBaseContainer {

  @Input() hourType: string[];

  constructor(
    protected route: ActivatedRoute,
    protected appFacade: AppFacade,
    protected cdr: ChangeDetectorRef,
    private readonly hotelResultsFacade: HotelResultsFacade,
    private readonly domManipulateService: DomManipulateService,
    private readonly router: Router,
  ) {
    super(route, appFacade, cdr);
  }

  get barFilters$(): Observable<HotelFilterVm[]> {
    return this.filters$.pipe(switchMap(filters =>
      zip(this.tagTypeIsPrime$, this.tagTypeIsRappicard$).pipe(
        map(([isPrime, hasRappiCard]) => {
          return getFiltersByBarWithCriteria(filters, isPrime, hasRappiCard);
        })
      )));
  }

  get hasFilters$(): Observable<boolean> {
    return this.barFilters$.pipe(map(filters => !isEmpty(filters)));
  }

  get filters$(): Observable<HotelFilterVm[]> {
    return this.hotelResultsFacade.hotelFilters$;
  }

  get showPackHoursFilter(): boolean {
    return this.route?.snapshot?.data?.['isByhour'] && !this.isMapView;
  }

  get isMapView(): boolean {
    return this.viewType === HotelFilterProvider.HotelMap;
  }

  public exclusiveOfferFilterAction(): void {
    const payload = this.pickedMemberRatesOnly ?
      { type: HotelFilterTypes.MemberRatesOnly, value: false } :
      { type: HotelFilterTypes.MemberRatesOnly, value: true };
    this.applyHotelFilterVmBy(payload);
  }

  public checkboxAction(event: { filter: HotelFilterVm, isMultiple: boolean, checked: boolean, isCheckbox: boolean }): void {
    if (event.isMultiple) {
      this.toggleIndividualHotelFilter(event.filter, []);
    } else if (event.checked) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.removeCheckboxFilter((event.filter.value as any[])[0], event.filter.type, event.isCheckbox);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.applyHotelFilterVmBy({ type: event.filter.type, value: (event.filter.value as any[])[0].Text });
    }
  }

  public radioAction(event: { filter: HotelFilterVm, isMultiple: boolean }): void {
    if (event.isMultiple) {
      this.toggleIndividualHotelFilter(event.filter, null);
    }
  }

  public removeCheckboxFilter(item: CheckboxFilter, type: string, isCheckboxFilter: boolean): void {
    const payload = isCheckboxFilter ? { type: type as HotelFilterTypes, value: item.Text, removable: true } : {
      type: type as HotelFilterTypes, value: this.selectedStarItems.filter(_item => _item.Text !== item.Text)
    };
    this.applyHotelFilterVmBy(payload);
  }

  public toggleIndividualHotelFilter(filter: HotelFilterVm, resetValue: HotelFilterApplierValue): void {
    if (filter) {
      this.router.navigate([{ outlets: { 'outlet': [singleFilterRouteByHotels] } }], {
        state: {
          filter: filter, isHotelByPackage: false,
          resetValue: resetValue, viewType: this.viewType,
        }, relativeTo: this.route, skipLocationChange: true
      });
    }
  }

  public innerRemoveByStaticHotel(filter: HotelFilterApplier): void {
    this.applyHotelFilterVmBy(filter);
  }

  public sharedShouldShowFiltersByHotel(value: boolean): void {
    this.hotelResultsFacade.sortingHasBeenChanged$.pipe(
      take(1),
      tap((sortingHasBeenChanged) => {
        this.sharedShouldShowFilters(value || sortingHasBeenChanged);
      })
    ).subscribe().unsubscribe();
  }

  public applyHotelFilterVmBy(filter: HotelFilterApplier): void {
    if (this.viewType === HotelFilterProvider.HotelResults) {
      this.domManipulateService.resetScrollPosition();
      this.hotelResultsFacade.fetchHotelsWithFilters(filter);
    } else {
      this.hotelResultsFacade.fetchHotelsMarkersWithFilters(filter);
    }
  }

}
